import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { IPublicLayout, IPublicPlotView } from "../types";
import tenantConfig from "config/tenant";
import "./publicPlotView.scss";
import {
  BRAND,
  PLOT_FILL,
  PLOT_STROKE,
  PLOT_TEXT,
  LEGEND_META,
} from "../colors";

const BASE_URL = process.env.REACT_APP_BASE_URL ?? "";

const STATUS_CLASS: Record<string, string> = {
  AVAILABLE: "ppv-status--available",
  RESERVED: "ppv-status--reserved",
  BOOKED: "ppv-status--booked",
  SOLD: "ppv-status--sold",
  BLOCKED: "ppv-status--blocked",
  ON_HOLD: "ppv-status--on-hold",
};

function coordsToPoints(coords: { x: number; y: number }[]): string {
  return coords.map((p) => `${p.x},${p.y}`).join(" ");
}

// ── Compass rose ──────────────────────────────────────────────────────────────
const CompassRose: React.FC<{ cx: number; cy: number; r: number }> = ({
  cx,
  cy,
  r,
}) => (
  <g>
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill="white"
      fillOpacity={0.92}
      stroke="#d1d5db"
      strokeWidth={0.6}
    />
    <polygon
      points={`${cx},${cy - r + 3} ${cx - r * 0.35},${cy + r * 0.3} ${cx + r * 0.35},${cy + r * 0.3}`}
      fill={BRAND.navy}
    />
    <polygon
      points={`${cx},${cy + r - 3} ${cx - r * 0.35},${cy - r * 0.3} ${cx + r * 0.35},${cy - r * 0.3}`}
      fill="#cbd5e1"
    />
    <text
      x={cx}
      y={cy - r + 2}
      textAnchor="middle"
      dominantBaseline="auto"
      fontSize={r * 0.45}
      fontWeight={700}
      fill={BRAND.navy}
    >
      N
    </text>
  </g>
);

// ── Layout map ────────────────────────────────────────────────────────────────
interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const LayoutMapView: React.FC<{
  layout: IPublicLayout;
  selectedId?: string;
  plotNumber: string;
}> = ({ layout, selectedId, plotNumber }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [vb, setVb] = useState<ViewBox>({
    x: 0,
    y: 0,
    w: layout.canvasWidth,
    h: layout.canvasHeight,
  });
  const dragRef = useRef<{
    sx: number;
    sy: number;
    ox: number;
    oy: number;
  } | null>(null);

  const handleWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const svgX = vb.x + (mx / rect.width) * vb.w;
    const svgY = vb.y + (my / rect.height) * vb.h;
    const factor = e.deltaY > 0 ? 1.12 : 0.89;
    const nw = Math.max(5, Math.min(layout.canvasWidth * 4, vb.w * factor));
    const nh = Math.max(5, Math.min(layout.canvasHeight * 4, vb.h * factor));
    setVb({
      x: svgX - (mx / rect.width) * nw,
      y: svgY - (my / rect.height) * nh,
      w: nw,
      h: nh,
    });
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: vb.x, oy: vb.y };
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = ((e.clientX - dragRef.current.sx) / rect.width) * vb.w;
    const dy = ((e.clientY - dragRef.current.sy) / rect.height) * vb.h;
    setVb((prev) => ({
      ...prev,
      x: dragRef.current!.ox - dx,
      y: dragRef.current!.oy - dy,
    }));
  };

  const handleMouseUp = () => {
    dragRef.current = null;
  };

  const zoomIn = () =>
    setVb((v) => {
      const f = 0.75;
      return {
        x: v.x + (v.w * (1 - f)) / 2,
        y: v.y + (v.h * (1 - f)) / 2,
        w: v.w * f,
        h: v.h * f,
      };
    });
  const zoomOut = () =>
    setVb((v) => {
      const f = 1.33;
      return {
        x: v.x - (v.w * (f - 1)) / 2,
        y: v.y - (v.h * (f - 1)) / 2,
        w: Math.min(v.w * f, layout.canvasWidth * 4),
        h: Math.min(v.h * f, layout.canvasHeight * 4),
      };
    });
  const fitAll = () =>
    setVb({ x: 0, y: 0, w: layout.canvasWidth, h: layout.canvasHeight });

  const statuses = Array.from(
    new Set(
      layout.plots
        .map((p) => p.status.toUpperCase())
        .filter((s) => PLOT_STROKE[s]),
    ),
  );
  const cr = Math.min(layout.canvasWidth, layout.canvasHeight) * 0.06;
  const fsSm = Math.min(layout.canvasWidth, layout.canvasHeight) * 0.05;
  const fsMed = Math.min(layout.canvasWidth, layout.canvasHeight) * 0.06;
  const fsLg = Math.min(layout.canvasWidth, layout.canvasHeight) * 0.07;

  return (
    <div className="ppv-minimap">
      <div className="ppv-minimap__label">Layout Map</div>
      <div className="ppv-map-wrap">
        <svg
          ref={svgRef}
          viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
          width="100%"
          className="ppv-map-svg"
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Light base */}
          <rect
            x={vb.x - 1000}
            y={vb.y - 1000}
            width={vb.w + 2000}
            height={vb.h + 2000}
            fill="#f0f4f8"
          />

          {/* Open spaces — soft green */}
          {layout.openSpaces.map((os) => {
            if (!os.coordinates?.length) return null;
            return (
              <g key={os.id}>
                <polygon
                  points={coordsToPoints(os.coordinates)}
                  fill="#dcfce7"
                  stroke="#86efac"
                  strokeWidth={0.6}
                />
                {os.labelX != null && os.labelY != null && (
                  <text
                    x={os.labelX}
                    y={os.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fsMed}
                    fontWeight={600}
                    fill="#166534"
                  >
                    {os.spaceName ?? "Open Space"}
                  </text>
                )}
              </g>
            );
          })}

          {/* Roads — the layout API sends each road as a single anchor point
              plus a name and CAD angle. With ≥2 points we draw the corridor as
              a polyline; with a single point we just place the rotated name
              label (the corridor reads as the gap between plot blocks). */}
          {layout.roads.map((road, i) => {
            const pts = Array.isArray(road.coordinates)
              ? road.coordinates
              : road.coordinates
                ? [road.coordinates]
                : [];
            if (pts.length === 0) return null;
            const sw = road.strokeWidth ?? 6;
            const midX = pts.reduce((s, p) => s + p.x, 0) / pts.length;
            const midY = pts.reduce((s, p) => s + p.y, 0) / pts.length;
            // Polyline tilt when we have a path; otherwise the supplied CAD
            // angle for the lone label.
            const ang =
              pts.length >= 2
                ? Math.atan2(
                    pts[pts.length - 1].y - pts[0].y,
                    pts[pts.length - 1].x - pts[0].x,
                  ) *
                  (180 / Math.PI)
                : -(road.angle ?? 0);
            const ptStr = coordsToPoints(pts);
            return (
              <g key={road.id ?? `road-${i}`}>
                {pts.length >= 2 && (
                  <>
                    <polyline
                      points={ptStr}
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth={sw}
                      strokeLinecap="round"
                    />
                    <polyline
                      points={ptStr}
                      fill="none"
                      stroke="#d1d5db"
                      strokeWidth={sw * 0.6}
                      strokeLinecap="round"
                    />
                    <polyline
                      points={ptStr}
                      fill="none"
                      stroke="white"
                      strokeWidth={sw * 0.15}
                      strokeLinecap="round"
                      strokeDasharray={`${sw * 0.5} ${sw * 0.5}`}
                    />
                  </>
                )}
                {road.roadName && (
                  <text
                    x={midX}
                    y={midY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fsMed}
                    fontWeight={600}
                    fill="#374151"
                    transform={`rotate(${ang},${midX},${midY})`}
                  >
                    {road.roadName}
                  </text>
                )}
              </g>
            );
          })}

          {/* Non-selected plots */}
          {layout.plots.map((p) => {
            if (p.id === selectedId) return null;
            if (!p.coordinates?.length) return null;
            const status = p.status.toUpperCase();
            return (
              <g key={p.id}>
                <polygon
                  points={coordsToPoints(p.coordinates)}
                  fill={PLOT_FILL[status] ?? "#f8fafc"}
                  stroke={PLOT_STROKE[status] ?? "#cbd5e1"}
                  strokeWidth={0.6}
                />
                {p.labelX != null && p.labelY != null && (
                  <text
                    x={p.labelX}
                    y={p.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fsSm}
                    fontWeight={600}
                    fill={PLOT_TEXT[status] ?? "#64748b"}
                  >
                    {p.plotNumber}
                  </text>
                )}
              </g>
            );
          })}

          {/* Selected plot — highlighted blue */}
          {(() => {
            const sel = layout.plots.find((p) => p.id === selectedId);
            if (!sel?.coordinates?.length) return null;
            const pts = sel.coordinates;
            return (
              <g>
                <polygon
                  points={coordsToPoints(pts)}
                  fill="#dbeafe"
                  stroke="#3b82f6"
                  strokeWidth={1.2}
                />
                {sel.labelX != null && sel.labelY != null && (
                  <text
                    x={sel.labelX}
                    y={sel.labelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={fsLg}
                    fontWeight={700}
                    fill="#1d4ed8"
                  >
                    {plotNumber}
                  </text>
                )}
              </g>
            );
          })()}

          {/* Compass */}
          <CompassRose
            cx={vb.x + vb.w - cr * 1.5}
            cy={vb.y + cr * 1.5}
            r={cr}
          />
        </svg>

        <div className="ppv-map-controls">
          <button className="ppv-map-ctrl-btn" onClick={zoomIn} type="button">
            +
          </button>
          <button className="ppv-map-ctrl-btn" onClick={zoomOut} type="button">
            −
          </button>
          <button
            className="ppv-map-ctrl-btn"
            onClick={fitAll}
            type="button"
            title="Fit all"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        </div>
      </div>

      {statuses.length > 0 && (
        <div className="ppv-minimap__roads">
          {statuses.map((s) => (
            <div key={s} className="ppv-minimap__road-row">
              <span
                className="ppv-legend-dot"
                style={{ background: LEGEND_META[s]?.dot }}
              />
              <span className="ppv-minimap__road-name">
                {LEGEND_META[s]?.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const PublicPlotView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [plot, setPlot] = useState<IPublicPlotView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${BASE_URL}/public/layout/plot/${token}`)
      .then((res) => setPlot(res.data?.data ?? null))
      .catch((err) => {
        const msg =
          err.response?.data?.message ?? "This link is invalid or has expired.";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="ppv">
      <header className="ppv-header">
        <div className="ppv-header__brand">
          <img
            src={tenantConfig.logoIcon}
            alt={tenantConfig.name}
            className="ppv-header__logo"
          />
          <span className="ppv-header__name">{tenantConfig.name}</span>
        </div>
        {plot && (
          <span className={`ppv-status ${STATUS_CLASS[plot.status] ?? ""}`}>
            {plot.status.charAt(0) + plot.status.slice(1).toLowerCase()}
          </span>
        )}
      </header>

      <div className="ppv-body">
        {loading && (
          <div className="ppv-loading">
            <div className="ppv-loading__spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="ppv-error">
            <div className="ppv-error__icon">⚠️</div>
            <h2 className="ppv-error__title">Link unavailable</h2>
            <p className="ppv-error__msg">{error}</p>
          </div>
        )}

        {!loading && plot && (
          <>
            <div className="ppv-hero">
              
              {plot.projectName && (
                <div className="ppv-hero__project">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  {plot.projectName}
                </div>)}
                
                <div className="ppv-hero__number">Plot {plot.plotNumber}</div>
              {plot.plotLabel && (
                <div className="ppv-hero__label">{plot.plotLabel}</div>
              )}

              {(plot.project?.city || plot.project?.state) && (
                <div className="ppv-hero__location">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 21s-6-4.35-6-10a6 6 0 1 1 12 0c0 5.65-6 10-6 10z" />
                    <circle cx="12" cy="11" r="2.5" />
                  </svg>

                  {[plot.project?.city, plot.project?.state]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
            </div>

            {plot.layout && (
              <LayoutMapView
                layout={plot.layout}
                selectedId={plot.selectedPlotId}
                plotNumber={plot.plotNumber}
              />
            )}
          </>
        )}
      </div>

      <footer className="ppv-footer">
        &copy; {new Date().getFullYear()} {tenantConfig.copyrightHolder} · All rights reserved
      </footer>
    </div>
  );
};

export default PublicPlotView;
